import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { sellersAPI, Listing, followsAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import ListingCard from '../components/ListingCard';
import DisplayStarRating from '../components/DisplayStarRating';
import { createSlug } from '../utils/slug';
import {
  formatStoreTime,
  getStoreOpenStatus,
  getTodayStoreHours,
  hoursUnavailableDetailLabel,
  parseStoreHours,
} from '../utils/listingDeliveryInfo';

interface Seller {
  id: number;
  username: string;
  email: string;
  member_since: string;
  profile_picture?: string;
  background_image?: string;
  seller_hours?: string | Record<string, { open: string; close: string; closed: boolean }> | null;
  seller_open_time?: string | null;
  seller_close_time?: string | null;
}

interface Rating {
  id: number;
  rating: number;
  comment: string | null;
  created_at: string;
  buyer_name: string;
}

interface Store {
  id: number;
  name: string;
  address: string;
  close_time?: string;
  state_name: string;
  state_abbreviation: string;
  city_name: string;
}

interface SellerProfile {
  seller: Seller;
  ratings: {
    average: number;
    total: number;
    positive_percentage: number;
    formatted_count: string;
    distribution: {
      5: number;
      4: number;
      3: number;
      2: number;
      1: number;
    };
    reviews: Rating[];
    detailed_ratings?: {
      accurate_description: number;
      shipping_speed: number;
      communication: number;
    };
  };
  badges?: {
    is_top_rated_plus: boolean;
    has_fast_shipping: boolean;
    has_tracking: boolean;
    returns_money_back: boolean;
  };
  listings: Listing[];
  stores?: Store[];
}

const SellerProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [profile, setProfile] = useState<SellerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [itemQuantities, setItemQuantities] = useState<Map<number, number>>(new Map());
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [checkingFollowStatus, setCheckingFollowStatus] = useState(true);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [showAllListings, setShowAllListings] = useState(false);
  const [listingsDisplayedCount, setListingsDisplayedCount] = useState(6);
  const listingsPageSize = 12;
  const [sellerFeedback, setSellerFeedback] = useState<{ last_12_months: { positive: number; neutral: number; negative: number } } | null>(null);
  const addingBarTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isOwner = user && profile && user.id === profile.seller.id;

  useEffect(() => {
    if (id) {
      loadProfile();
      if (user && !isOwner) {
        checkFollowStatus();
        loadFollowerCount();
      } else {
        setCheckingFollowStatus(false);
      }
    }
  }, [id, user]);

  useEffect(() => {
    if (profile?.seller?.id && (profile.ratings?.total ?? 0) > 0) {
      sellersAPI.getFeedback(profile.seller.id)
        .then((data: any) => setSellerFeedback(data))
        .catch(() => setSellerFeedback(null));
    } else {
      setSellerFeedback(null);
    }
  }, [profile?.seller?.id, profile?.ratings?.total]);

  const checkFollowStatus = async () => {
    if (!user || !id || isOwner) return;
    try {
      setCheckingFollowStatus(true);
      const data = await followsAPI.checkUserFollowStatus(parseInt(id));
      setIsFollowing(data.isFollowing);
    } catch (err) {
      console.error('Error checking follow status:', err);
    } finally {
      setCheckingFollowStatus(false);
    }
  };

  const loadFollowerCount = async () => {
    if (!id) return;
    try {
      const data = await followsAPI.getUserFollowerCount(parseInt(id));
      setFollowerCount(data.count);
    } catch (err) {
      console.error('Error loading follower count:', err);
    }
  };

  const handleFollow = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!id) return;

    try {
      if (isFollowing) {
        await followsAPI.unfollowUser(parseInt(id));
        setIsFollowing(false);
        setFollowerCount(prev => Math.max(0, prev - 1));
      } else {
        await followsAPI.followUser(parseInt(id));
        setIsFollowing(true);
        setFollowerCount(prev => prev + 1);
      }
    } catch (err: any) {
      console.error('Error following/unfollowing user:', err);
      toast.error(err.response?.data?.error || 'Failed to update follow status');
    }
  };


  const loadProfile = async () => {
    try {
      const data = await sellersAPI.getProfile(parseInt(id!));
      
      // Ensure ratings object exists with default structure if missing
      if (data && !data.ratings) {
        data.ratings = {
          average: 0,
          total: 0,
          positive_percentage: 0,
          formatted_count: '0',
          distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
          reviews: [],
          detailed_ratings: {
            accurate_description: 0,
            shipping_speed: 0,
            communication: 0,
          },
        };
      }
      
      // Ensure reviews is always an array
      if (data && data.ratings && !Array.isArray(data.ratings.reviews)) {
        data.ratings.reviews = [];
      }
      
      setProfile(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load seller profile');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600">Loading seller profile...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600">{error || 'Seller not found'}</p>
          <Link
            to="/"
            className="mt-4 inline-block px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            Go Back
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Banner - Full Width on Mobile, Contained on Desktop */}
      <div className="relative w-full md:max-w-6xl md:mx-auto md:px-4 h-56 sm:h-64 md:h-72 overflow-hidden md:mt-6 mb-4 md:mb-6 md:rounded-2xl">
        {profile.seller.background_image ? (
          <img 
            src={profile.seller.background_image} 
            alt="Background" 
            className="w-full h-full object-cover md:rounded-2xl"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-green-400 to-green-600 md:rounded-2xl"></div>
        )}
        
      </div>

      {/* Content Container */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Profile Picture - Half Overlapping Banner */}
        <div className="relative -mt-12 sm:-mt-16 md:-mt-20 mb-4">
          <div className="relative inline-block">
            {profile.seller.profile_picture ? (
              <img
                src={profile.seller.profile_picture}
                alt={profile.seller.username}
                className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full border-4 border-white object-cover shadow-md"
              />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 bg-green-500 rounded-full border-4 border-white flex items-center justify-center text-white text-2xl sm:text-3xl md:text-4xl font-bold shadow-md">
                {profile.seller.username.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
        </div>

        {/* Seller Name and Info - Below Profile Picture */}
        <div className="mb-4">
          <div className="flex items-start justify-between gap-4 mb-1.5">
            <div className="flex-1">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-1.5">
                {profile.seller.username}
              </h2>
              
              {/* Member Since */}
              <div className="text-xs sm:text-sm text-gray-500 mb-2">
                Member since {formatDate(profile.seller.member_since)}
              </div>
              
              {/* Service Tags */}
              <div className="flex flex-wrap gap-2 mb-3">
                {profile.badges?.has_fast_shipping && (
                  <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-xs sm:text-sm font-medium">
                    Delivery
                  </span>
                )}
                {profile.badges?.has_tracking && (
                  <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-xs sm:text-sm font-medium">
                    With tracking
                  </span>
                )}
                {profile.badges?.returns_money_back && (
                  <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-xs sm:text-sm font-medium">
                    Returns, money back
                  </span>
                )}
              </div>
              
              {/* Ratings and Info */}
              <div className="space-y-1.5">
                {profile.ratings && profile.ratings.total > 0 ? (
                  <div className="flex flex-wrap items-center gap-1.5 text-sm">
                    <span className="font-medium text-green-600">
                      {Number(profile.ratings.positive_percentage ?? 100).toFixed(
                        Number(profile.ratings.positive_percentage ?? 100) % 1 === 0 ? 0 : 1
                      )}% Buyer Approved
                    </span>
                    <span className="text-gray-500">
                      ({profile.ratings.total} {profile.ratings.total === 1 ? 'rating' : 'ratings'})
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-1.5 text-sm">
                    <span className="text-gray-500">No ratings yet</span>
                  </div>
                )}
                {followerCount > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 text-sm text-gray-500">
                    <i className="ri-user-follow-line"></i>
                    <span>{followerCount} {followerCount === 1 ? 'follower' : 'followers'}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right Side - Edit, Follow, and Message Seller Buttons */}
            <div className="ml-auto flex gap-2">
              {isOwner && (
                <Link
                  to="/sell"
                  className="bg-green-600 hover:bg-green-700 text-white font-medium px-3 py-1.5 rounded-lg transition text-xs sm:text-sm"
                >
                  Manage in app
                </Link>
              )}
              {user && user.id !== parseInt(id!) && (
                <>
                  <button
                    onClick={handleFollow}
                    disabled={checkingFollowStatus}
                    className={`px-3 sm:px-4 py-1.5 font-medium rounded-lg transition flex items-center gap-2 text-xs sm:text-sm ${
                      isFollowing
                        ? 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                        : 'bg-green-500 hover:bg-green-600 text-white'
                    } ${checkingFollowStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {checkingFollowStatus ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        <span className="hidden sm:inline">Loading...</span>
                      </>
                    ) : isFollowing ? (
                      <>
                        <i className="ri-user-follow-fill"></i>
                        <span className="hidden sm:inline">Following</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-user-add-line"></i>
                        <span className="hidden sm:inline">Follow</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => navigate('/messages', { state: { sellerId: parseInt(id!) } })}
                    className="px-3 sm:px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition text-xs sm:text-sm flex items-center gap-2"
                  >
                    <i className="ri-message-3-line"></i>
                    <span className="hidden sm:inline">Message Seller</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        {profile.ratings && profile.ratings.total > 0 && profile.ratings.reviews && profile.ratings.reviews.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Reviews & Ratings</h2>
            
            <div className="flex flex-col md:flex-row md:gap-8 md:pb-6 md:border-b md:border-gray-200">
              <div className="flex-1 space-y-3 pb-6 md:pb-0 border-b md:border-b-0 border-gray-200 md:border-0">
                {(() => {
                  const pos = sellerFeedback?.last_12_months?.positive ?? 0;
                  const neu = sellerFeedback?.last_12_months?.neutral ?? 0;
                  const neg = sellerFeedback?.last_12_months?.negative ?? 0;
                  // Always use server all-time % so header / listing / profile match.
                  const positivePct = Number(profile.ratings.positive_percentage ?? 0).toFixed(
                    Number(profile.ratings.positive_percentage ?? 0) % 1 === 0 ? 0 : 1
                  );
                  return (
                    <>
                      <div className="flex items-center gap-3">
                        <span className="text-4xl font-bold text-gray-900">
                          {profile.ratings.average.toFixed(2)}
                        </span>
                        <DisplayStarRating rating={profile.ratings.average} />
                      </div>
                      <p className="text-sm text-gray-700 flex items-center gap-1.5">
                        <i className="ri-checkbox-circle-fill text-green-500 text-base" />
                        <span>
                          {positivePct}% Buyer Approved • {profile.ratings.total}{' '}
                          {profile.ratings.total === 1 ? 'review' : 'reviews'}
                        </span>
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-900">
                          Approved {pos.toLocaleString()}
                        </span>
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                          Neutral {neu.toLocaleString()}
                        </span>
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
                          Not approved {neg.toLocaleString()}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="hidden md:block w-px bg-gray-200 self-stretch shrink-0" />

              {profile.ratings?.detailed_ratings && (
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-gray-900 mb-0.5">Detailed ratings</h3>
                  <p className="text-xs text-gray-500 mb-3">Average for the last 12 months</p>
                  <div className="space-y-2.5">
                    {(profile.ratings.detailed_ratings?.accurate_description ?? 0) > 0 && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-gray-700">Accurate description</span>
                        <DisplayStarRating rating={Number(profile.ratings.detailed_ratings?.accurate_description ?? 0)} size="sm" />
                      </div>
                    )}
                    {(profile.ratings.detailed_ratings?.shipping_speed ?? 0) > 0 && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-gray-700">Shipping speed</span>
                        <DisplayStarRating rating={Number(profile.ratings.detailed_ratings?.shipping_speed ?? 0)} size="sm" />
                      </div>
                    )}
                    {(profile.ratings.detailed_ratings?.communication ?? 0) > 0 && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-gray-700">Communication</span>
                        <DisplayStarRating rating={Number(profile.ratings.detailed_ratings?.communication ?? 0)} size="sm" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="pt-6">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <h3 className="text-lg font-bold text-gray-900">Customer reviews</h3>
                {profile.ratings?.reviews && profile.ratings.reviews.length > 5 && (
                  <button
                    type="button"
                    onClick={() => setShowAllReviews((v) => !v)}
                    className="text-sm font-medium text-green-600 hover:text-green-700"
                  >
                    {showAllReviews ? 'Show less' : `View all reviews (${profile.ratings.reviews.length})`}
                  </button>
                )}
              </div>
              <div className="space-y-4">
                {profile.ratings && profile.ratings.reviews && profile.ratings.reviews.length > 0 ? (
                  (showAllReviews ? profile.ratings.reviews : profile.ratings.reviews.slice(0, 5)).map((review: any) => (
                    <div key={review.id} className="border-b border-gray-200 pb-4 last:border-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">
                            {review.buyer_name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{review.buyer_name}</p>
                            <p className="text-xs text-gray-500">{formatDate(review.created_at)}</p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <DisplayStarRating rating={review.rating} />
                        </div>
                      </div>
                      {(review.comment || review.message) && (
                        <p className="text-gray-700 mt-2 ml-12">{review.comment || review.message}</p>
                      )}
                      {(review.accurate_description || review.shipping_speed || review.communication) && (
                        <div className="mt-3 pt-3 border-t border-gray-100 ml-12">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                            {review.accurate_description && (
                              <div>
                                <span className="text-gray-600">Accurate description: </span>
                                <span className="font-medium text-gray-900">
                                  {Number(review.accurate_description).toFixed(1)}
                                </span>
                                <div className="flex items-center mt-1">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                      key={star}
                                      className={`text-xs ${
                                        star <= Number(review.accurate_description)
                                          ? 'text-yellow-400'
                                          : 'text-gray-300'
                                      }`}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {review.shipping_speed && (
                              <div>
                                <span className="text-gray-600">Shipping speed: </span>
                                <span className="font-medium text-gray-900">
                                  {Number(review.shipping_speed).toFixed(1)}
                                </span>
                                <div className="flex items-center mt-1">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                      key={star}
                                      className={`text-xs ${
                                        star <= Number(review.shipping_speed)
                                          ? 'text-yellow-400'
                                          : 'text-gray-300'
                                      }`}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {review.communication && (
                              <div>
                                <span className="text-gray-600">Communication: </span>
                                <span className="font-medium text-gray-900">
                                  {Number(review.communication).toFixed(1)}
                                </span>
                                <div className="flex items-center mt-1">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                      key={star}
                                      className={`text-xs ${
                                        star <= Number(review.communication)
                                          ? 'text-yellow-400'
                                          : 'text-gray-300'
                                      }`}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No reviews yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stores */}
        {profile.stores && profile.stores.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
              Stores ({profile.stores.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {profile.stores.map((store) => (
                <Link
                  key={store.id}
                  to={`/stores/${createSlug(store.name)}`}
                  className="bg-gray-50 rounded-lg shadow-md p-6 hover:shadow-lg transition border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-green-500 rounded-lg flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                      <i className="ri-store-line"></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{store.name}</h3>
                      <p className="text-sm text-gray-600 mb-2">{store.address}</p>
                      <p className="text-xs text-gray-500">
                        {store.city_name}, {store.state_abbreviation}
                      </p>
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-400 text-xl"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {(() => {
          const hoursSource = {
            hours: profile.seller.seller_hours ?? null,
            open_time: profile.seller.seller_open_time ?? null,
            close_time: profile.seller.seller_close_time ?? null,
          };
          const hoursObj = parseStoreHours(hoursSource);
          if (!hoursObj) return null;
          const openStatus = getStoreOpenStatus(hoursSource);
          const todayHours = getTodayStoreHours(hoursSource);
          return (
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Seller hours</h2>
                {openStatus.isOpen === true && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Available
                  </span>
                )}
                {openStatus.isOpen === false && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    Not available
                  </span>
                )}
              </div>
              {openStatus.isOpen === false && (
                <p className="text-sm text-gray-600 mb-3">
                  {hoursUnavailableDetailLabel(hoursSource, 'seller')}
                </p>
              )}
              {openStatus.isOpen === true && todayHours && !todayHours.closed && todayHours.open && todayHours.close && (
                <p className="text-sm text-gray-600 mb-3">
                  {formatStoreTime(todayHours.open)} – {formatStoreTime(todayHours.close)}
                </p>
              )}
              <div className="space-y-2">
                {Object.entries(hoursObj).map(([day, { open, close, closed }]) => (
                  <div key={day} className="flex items-center justify-between py-1 text-sm border-b border-gray-50 last:border-0">
                    <span className="text-gray-700 font-medium">{day}</span>
                    {closed ? (
                      <span className="text-gray-500">Closed</span>
                    ) : (
                      <span className="text-gray-900">
                        {formatStoreTime(open)} – {formatStoreTime(close)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Listings */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              Items for Sale
              {profile.listings && profile.listings.length > 0 && (
                <span className="text-base sm:text-lg font-normal text-gray-500 ml-2">
                  ({profile.listings.length})
                </span>
              )}
            </h2>
            {profile.listings.length > 6 && (
              <button
                type="button"
                onClick={() => {
                  if (showAllListings) {
                    setShowAllListings(false);
                    setListingsDisplayedCount(6);
                  } else {
                    setShowAllListings(true);
                    setListingsDisplayedCount(Math.min(6 + listingsPageSize, profile.listings.length));
                  }
                }}
                className="text-sm font-medium text-green-600 hover:text-green-700"
              >
                {showAllListings ? 'Show less' : 'View all'}
              </button>
            )}
          </div>
          {profile.listings.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <p className="text-gray-600">No listings available</p>
            </div>
          ) : (
            <>
              {user && selectedItems.size > 0 && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                  <span className="text-green-800 font-medium">
                    {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={() => {
                      const listingsToCheckout: Listing[] = [];
                      profile.listings.forEach((l: Listing) => {
                        if (selectedItems.has(l.id) && 
                            (l.quantity === undefined || l.quantity > 0) && 
                            !(l.sold === 1 || l.sold === true)) {
                          const qty = itemQuantities.get(l.id) || 1;
                          // Add the listing multiple times based on quantity
                          for (let i = 0; i < qty; i++) {
                            listingsToCheckout.push({ ...l });
                          }
                        }
                      });
                      if (listingsToCheckout.length > 0) {
                        navigate('/checkout', { state: { listings: listingsToCheckout } });
                      }
                    }}
                    className="bg-green-500 hover:bg-green-600 text-white font-medium px-6 py-2 rounded-lg transition"
                  >
                    Checkout Selected ({selectedItems.size})
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
                {profile.listings.slice(0, showAllListings ? listingsDisplayedCount : 6).map((listing) => {
                  const isSelected = selectedItems.has(listing.id);
                  const isSold = listing.sold === 1 || listing.sold === true;
                  const isOutOfStock = listing.quantity !== undefined && listing.quantity <= 0;
                  const isOwner = user && user.id === listing.user_id;
                  // Show button if item is available (not sold, not out of stock)
                  const canSelect = !isSold && !isOutOfStock;
                  const currentQuantity = itemQuantities.get(listing.id) || 1;
                  const hasMultipleQuantity = listing.quantity !== undefined && listing.quantity > 1;
                  
                  const handleAddToCart = () => {
                    if (!user) {
                      navigate('/login');
                      return;
                    }
                    if (isOwner) {
                      // Show message that you can't add your own item
                      const messageBar = document.createElement('div');
                      messageBar.id = 'own-item-message';
                      messageBar.style.cssText = `
                        position: fixed !important;
                        bottom: 0 !important;
                        left: 0 !important;
                        right: 0 !important;
                        z-index: 999999 !important;
                        background-color: #f59e0b !important;
                        color: white !important;
                        padding: 12px 16px !important;
                        display: flex !important;
                        align-items: center !important;
                        gap: 12px !important;
                        box-shadow: 0 -2px 10px rgba(0,0,0,0.2) !important;
                      `;
                      messageBar.innerHTML = '<i class="ri-information-line" style="font-size: 20px;"></i><span style="font-weight: 500; font-size: 16px;">You cannot add your own item to cart</span>';
                      document.body.appendChild(messageBar);
                      
                      setTimeout(() => {
                        const barToRemove = document.getElementById('own-item-message');
                        if (barToRemove) {
                          barToRemove.remove();
                        }
                      }, 2000);
                      return;
                    }
                    const quantity = itemQuantities.get(listing.id) || 1;
                    
                    // Clear any existing timeout and bar
                    if (addingBarTimeoutRef.current) {
                      clearTimeout(addingBarTimeoutRef.current);
                    }
                    const existingBar = document.getElementById('adding-bar-seller-profile');
                    if (existingBar) {
                      existingBar.remove();
                    }
                    
                    // Create bar directly in DOM - bypasses React completely
                    const bar = document.createElement('div');
                    bar.id = 'adding-bar-seller-profile';
                    bar.style.cssText = `
                      position: fixed !important;
                      bottom: 0 !important;
                      left: 0 !important;
                      right: 0 !important;
                      z-index: 999999 !important;
                      background-color: #ef4444 !important;
                      color: white !important;
                      padding: 12px 16px !important;
                      display: flex !important;
                      align-items: center !important;
                      gap: 12px !important;
                      box-shadow: 0 -2px 10px rgba(0,0,0,0.2) !important;
                    `;
                    bar.innerHTML = '<i class="ri-shopping-cart-line" style="font-size: 20px;"></i><span style="font-weight: 500; font-size: 16px;">Adding</span>';
                    document.body.appendChild(bar);
                    
                    // Also set React state for consistency
                    setIsAddingToCart(true);
                    
                    addToCart(listing, quantity);
                    
                    // Update selection state
                    const newSelected = new Set(selectedItems);
                    if (isSelected) {
                      newSelected.delete(listing.id);
                      const newQuantities = new Map(itemQuantities);
                      newQuantities.delete(listing.id);
                      setItemQuantities(newQuantities);
                    } else {
                      newSelected.add(listing.id);
                      setItemQuantities(new Map(itemQuantities).set(listing.id, 1));
                    }
                    setSelectedItems(newSelected);
                    
                    // Hide animation after delay
                    addingBarTimeoutRef.current = setTimeout(() => {
                      setIsAddingToCart(false);
                      const barToRemove = document.getElementById('adding-bar-seller-profile');
                      if (barToRemove) {
                        barToRemove.remove();
                      }
                      addingBarTimeoutRef.current = null;
                    }, 1500);
                  };
                  
                  return (
                    <div key={listing.id} className="relative">
                      {isSelected && hasMultipleQuantity && (
                        <div className="absolute top-2 left-2 z-30 bg-white rounded-lg border border-gray-200 shadow-lg p-2 flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              const newQty = Math.max(1, currentQuantity - 1);
                              setItemQuantities(new Map(itemQuantities).set(listing.id, newQty));
                            }}
                            className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
                          >
                            <i className="ri-subtract-line text-xs"></i>
                          </button>
                          <span className="text-sm font-semibold w-8 text-center">{currentQuantity}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              const maxQty = listing.quantity || 1;
                              const newQty = Math.min(maxQty, currentQuantity + 1);
                              setItemQuantities(new Map(itemQuantities).set(listing.id, newQty));
                            }}
                            className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
                          >
                            <i className="ri-add-line text-xs"></i>
                          </button>
                        </div>
                      )}
                      <ListingCard
                        showOpenStatus={false} 
                        listing={listing}
                        addButton={canSelect ? (
                          <button
                            type="button"
                            data-add-to-cart="true"
                            className={`add-to-cart-button w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg ${
                              isSelected 
                                ? 'bg-green-500 hover:bg-green-600 text-white scale-110' 
                                : 'bg-white text-black hover:bg-gray-50 hover:scale-110'
                            }`}
                            style={{
                              boxShadow: isSelected 
                                ? '0 8px 16px rgba(34, 197, 94, 0.4)' 
                                : '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)'
                            }}
                            title={isSelected ? 'Remove from cart' : 'Add to cart'}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              e.nativeEvent?.stopImmediatePropagation();
                              handleAddToCart();
                            }}
                          >
                            {isSelected ? (
                              <i className="ri-check-line text-lg font-bold"></i>
                            ) : (
                              <i className="ri-add-line text-base font-extrabold"></i>
                            )}
                          </button>
                        ) : undefined}
                      />
                    </div>
                  );
                })}
              </div>
              {showAllListings && listingsDisplayedCount < profile.listings.length && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setListingsDisplayedCount((prev) => Math.min(prev + listingsPageSize, profile.listings.length))}
                    className="text-sm font-medium text-green-600 hover:text-green-700 py-2"
                  >
                    Load more
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Adding to Cart Animation Bar */}
      {isAddingToCart && (
        <div 
          className="fixed bottom-0 left-0 right-0 bg-red-500 text-white px-4 py-3 flex items-center gap-3 z-[99999] animate-slide-up shadow-lg" 
          style={{ 
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 99999,
            backgroundColor: '#ef4444',
            color: 'white',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.2)'
          }}
        >
          <i className="ri-shopping-cart-line text-xl"></i>
          <span className="font-medium">Adding</span>
        </div>
      )}
    </div>
  );
};

export default SellerProfile;
